import { errorMessage, phoneSchema } from "@workspace/domain/src/utils";
import dayjs from "@workspace/domain/src/utils/dayjs";
import * as z from "zod";
import { AdminRole } from "../role/schema";

const EmployeeSchema = z.object({
  id: z.string(),

  agencyId: z.string(),
  roleId: z.string(),

  name: z.string(),
  username: z.string(),
  phoneNumber: z.string().nullable(),
  imageUrl: z.string().nullable(),
  isActive: z.boolean(),

  role: AdminRole.Role.omit({
    numberOfEmployees: true,
    numberOfPermissions: true,
  }),

  createdAt: z.date(),
  updatedAt: z.date(),
});

const SortBy = [
  "name",
  "createdAt",
  "username",
  "phone",
  "isActive",
  "roleId",
] as const;
const SortOrder = ["asc", "desc"] as const;

const GetSchema = z.object({
  id: z.string(),
});

const GetAllSchema = z
  .object({
    page: z.number(),
    limit: z.number(),

    sortBy: z.enum(SortBy),
    sortOrder: z.enum(SortOrder),
  })
  .extend(
    z
      .object({
        search: z.string(),
        roleIds: z.string().array(),
        isActive: z.boolean(),

        createdAtMin: z.union([
          z.iso.datetime().transform((date) => dayjs(date).toDate()),
          z.date(),
        ]),
        createdAtMax: z.union([
          z.iso.datetime().transform((date) => dayjs(date).toDate()),
          z.date(),
        ]),
      })
      .partial().shape,
  );

const PasswordSchema = z
  .string(errorMessage("Password is required", "Password is invalid"))
  .nonempty("Password is required")
  .toLowerCase()
  .max(255, "Password must be less than 255 characters");

const BaseSchema = z.object({
  username: z
    .string(errorMessage("Username is required", "Username is invalid"))
    .nonempty("Username is required")
    .toLowerCase()
    .max(255, "Username must be less than 255 characters"),
  password: PasswordSchema,
  name: z
    .string(errorMessage("Name is required", "Name is invalid"))
    .nonempty("Name is required")
    .max(255, "Name must be less than 255 characters"),
  phoneNumber: phoneSchema(
    "Phone number is required",
    "Phone number is invalid",
  ).optional(),
  roleId: z.string(errorMessage("Role is required", "Role is invalid")),
  isActive: z.boolean(errorMessage("Active is required", "Active is invalid")),
});

const CreateSchema = BaseSchema;

const UpdateSchema = BaseSchema.extend({
  ...GetSchema.shape,
});

const UpdatePasswordSchema = z.object({
  id: z.string(),
  password: PasswordSchema,
});

const DeleteSchema = GetSchema;

export const AdminEmployee = {
  Employee: EmployeeSchema,
  Get: GetSchema,
  GetAll: GetAllSchema,
  Create: CreateSchema,
  Update: UpdateSchema,
  UpdatePassword: UpdatePasswordSchema,
  Delete: DeleteSchema,
  SortBy: SortBy,
  SortOrder: SortOrder,
};
export type AdminEmployee = {
  Employee: z.infer<typeof EmployeeSchema>;
  Get: z.infer<typeof GetSchema>;
  GetAll: z.infer<typeof GetAllSchema>;
  Create: z.infer<typeof CreateSchema>;
  Update: z.infer<typeof UpdateSchema>;
  UpdatePassword: z.infer<typeof UpdatePasswordSchema>;
  Delete: z.infer<typeof DeleteSchema>;
  SortBy: (typeof SortBy)[number];
  SortOrder: (typeof SortOrder)[number];
};
